/* @moldura padrao */
/**
 * form.tsx (autorizações) — Nova autorização. Tema AdminUX. Rota /autorizacoes/nova.
 *
 * REFEITO EM 01/09/2026 (ficha #26): a autorização de desconto NASCE ANTES DA
 * OS — no orçamento do Tiny, que não está na API. Enquanto o pedido de venda
 * não existe, a loja fala dele pelo NOME DO CLIENTE e pelo número do orçamento.
 * Por isso o formulário pergunta primeiro DE ONDE vem o pedido (loja, tipo) e
 * SOBRE O QUÊ ele é (cliente/orçamento ou a OS) — e só depois o quanto e o
 * porquê. A ordem é a ordem em que quem pede pensa.
 *
 * O que o tipo declara manda no formulário (`exige_cliente`,
 * `pede_pedido_venda`): é por essas duas flags que o bloco do cliente aparece,
 * nunca por `nome === 'Desconto'` — tipo novo com o mesmo ciclo não pede
 * deploy.
 *
 * TODO CAMPO É `form-floating` — rótulo dentro da caixa. É o padrão de
 * formulário deste app (despesas, OS, viagens) e ele cobrou em 01/09 quando
 * esta tela saiu com rótulo fora: o formulário velho, que serviu de modelo,
 * já estava errado. Ver memória `formulario-rotulo-flutuante`.
 *
 * Este é o arranjo do escritório. O do celular (quem mais pede é o instalador)
 * vem depois, numa página própria se precisar — decisão dele em 01/09.
 */
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/auth/auth-context';
import {
  CustomCampoNumeroMoeda,
  CustomCampoNumeroPercentual,
} from '@/components/CustomCamposNumero';
import { paraBanco } from '@/lib/numero';
import type {
  AutorizacaoDetalhe,
  LojaOpcao,
  TipoOpcao,
  UsuarioOpcao,
} from './shared';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';
import { CustomCampoBusca, type ItemBusca } from '@/components/CustomCamposBusca';
import { ANCORA_OS, TIPO_GENERICA } from './shared';
import { CustomDica } from '@/components/CustomDica';

/** O que a lista de OS devolve, do pouco que este campo usa. */
interface OSListaItem { id: number; codigo?: string | null; cliente_nome?: string | null; cidade?: string | null }

/** Só dígitos: os números do Tiny são inteiros, e a coluna é BIGINT. */
const soDigitos = (v: string) => v.replace(/\D/g, '');

export function AutorizacaoFormPage() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [sp] = useSearchParams();
  const voltar = sp.get('voltar');
  const osLabelParam = sp.get('osLabel');

  const [tipos, setTipos] = useState<TipoOpcao[]>([]);
  const [lojas, setLojas] = useState<LojaOpcao[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioOpcao[]>([]);

  // De onde vem o pedido
  const [lojaId, setLojaId] = useState('');
  const [tipoId, setTipoId] = useState('');
  // Sobre o quê — as referências comerciais (078)
  const [clienteNome, setClienteNome] = useState('');
  const [fornecedorNome, setFornecedorNome] = useState('');
  const [numeroOrcamento, setNumeroOrcamento] = useState('');
  const [numeroPedidoVenda, setNumeroPedidoVenda] = useState('');
  const [osId, setOsId] = useState(sp.get('os') ?? '');
  /* O escolhido no campo de busca. Quem chega pela OS traz id e rótulo na URL,
     e o campo já abre preenchido — sem ir ao servidor para saber o nome. */
  const [osEscolhida, setOsEscolhida] = useState<ItemBusca | null>(
    sp.get('os') ? { id: Number(sp.get('os')), rotulo: osLabelParam ?? `OS ${sp.get('os')}` } : null,
  );

  async function buscarOS(termo: string): Promise<ItemBusca[]> {
    const achados = await api.get<OSListaItem[]>(
      `/ordens-servico?busca=${encodeURIComponent(termo)}&limite=10`,
    );
    return achados.map((o) => ({
      id: o.id,
      rotulo: o.codigo ?? `OS ${o.id}`,
      detalhe: [o.cliente_nome, o.cidade].filter(Boolean).join(' · ') || null,
    }));
  }
  // O pedido em si
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  // Valor Rápido: string canônica ('' | '80.66') — ver 11-campos-valor-rapido.md
  const [valor, setValor] = useState('');
  const [percentual, setPercentual] = useState('');
  // Em nome de quem
  const [solicitanteId, setSolicitanteId] = useState('');
  const [solicitanteNome, setSolicitanteNome] = useState('');

  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    api.get<TipoOpcao[]>('/autorizacoes/tipos').then(setTipos).catch(() => undefined);
    api.get<LojaOpcao[]>('/lojas').then(setLojas).catch(() => undefined);
    api.get<UsuarioOpcao[]>('/usuarios/opcoes').then(setUsuarios).catch(() => undefined);
  }, []);

  // A loja padrão de quem está logado já vem marcada — quase sempre é ela.
  useEffect(() => {
    if (!lojaId && usuario?.loja_id) setLojaId(String(usuario.loja_id));
  }, [usuario?.loja_id, lojaId]);

  const tipo = useMemo(
    () => tipos.find((t) => String(t.id) === tipoId) ?? null,
    [tipos, tipoId],
  );
  // O bloco do cliente aparece quando o tipo o exige OU quando vai pedir o
  // pedido de venda depois — nos dois casos a referência é o cliente.
  const pedeCliente = Boolean(tipo?.exige_cliente || tipo?.pede_pedido_venda);

  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (!lojaId && !osId) {
      toast.error('Informe a loja que está pedindo.');
      return;
    }
    if (pedeCliente && !clienteNome.trim()) {
      toast.error('Informe o cliente — é por ele que o pedido é reconhecido.');
      return;
    }
    if (!titulo.trim()) {
      toast.error('Informe um título para a autorização.');
      return;
    }
    setSalvando(true);
    try {
      const nova = await api.post<AutorizacaoDetalhe>('/autorizacoes', {
        tipo_id: tipoId ? Number(tipoId) : null,
        loja_id: lojaId ? Number(lojaId) : null,
        titulo: titulo.trim(),
        descricao: descricao.trim() || null,
        cliente_nome: clienteNome.trim() || null,
        fornecedor_nome: fornecedorNome.trim() || null,
        numero_orcamento: numeroOrcamento ? Number(numeroOrcamento) : null,
        numero_pedido_venda: numeroPedidoVenda ? Number(numeroPedidoVenda) : null,
        valor: paraBanco(valor),
        percentual: paraBanco(percentual),
        solicitante_id: solicitanteId ? Number(solicitanteId) : null,
        solicitante_nome: !solicitanteId && solicitanteNome.trim()
          ? solicitanteNome.trim()
          : null,
        entidade_tipo: osId ? ANCORA_OS : null,
        entidade_id: osId ? Number(osId) : null,
      });
      toast.success('Autorização registrada. A Adriana foi notificada.');
      navigate(voltar || `/autorizacoes/${nova.id}`);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : 'Não foi possível registrar.',
      );
    } finally {
      setSalvando(false);
    }
  }

  return (
    <CustomMolduraPagina
      titulo="Nova autorização"
      descricao="O pedido vai para a Adriana aprovar"
      caminho={[{ rotulo: 'Autorizações', para: '/autorizacoes' }]}
    >

      <form onSubmit={salvar}>
        <div className="row g-3">
          <div className="col-lg-8">
            <div className="card adminuiux-card mb-3">
              <div className="card-header bg-transparent">
                <h6 className="mb-0">O pedido</h6>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  {/* ── De onde vem ─────────────────────────────────────── */}
                  <div className="col-sm-4">
                    <div className="form-floating">
                      <select
                        id="aut-loja"
                        className="form-select"
                        value={lojaId}
                        onChange={(e) => setLojaId(e.target.value)}
                      >
                        <option value="">— escolha —</option>
                        {lojas.map((l) => (
                          <option key={l.id} value={String(l.id)}>
                            {l.nome_curto ?? l.nome}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="aut-loja">Loja *</label>
                    </div>
                  </div>
                  <div className="col-sm-8">
                    {/* ⛔ A LINHA DE BAIXO EXPLICA O QUE JÁ FOI ESCOLHIDO — boa
                        para confirmar, inútil para ESCOLHER, que é quando a
                        pessoa precisa COMPARAR. Dele, 11/09: *"dá para fazer um
                        tooltip que mostra os tipos que você pode escolher e o
                        que cada um quer dizer?"*.

                        Vai no rótulo, e não em cada `<option>`: num `<select>`
                        nativo quem desenha a lista é o navegador, e a página não
                        alcança as opções — `title` em `<option>` é ignorado na
                        maioria dos navegadores e não existe no toque. Trocar o
                        select por um menu próprio custaria muito mais do que
                        isto resolve. */}
                    <div className="d-flex align-items-start gap-1">
                    <div className="form-floating flex-grow-1">
                      <select
                        id="aut-tipo"
                        className="form-select"
                        value={tipoId}
                        onChange={(e) => setTipoId(e.target.value)}
                      >
                        {/* ⛔ "GENÉRICA" É CONCEITO DELE, e legítimo — eu tinha
                            tratado como resíduo e estava errado. Dele, 11/09:
                            *"quem inventou genérica fui eu: para dizer um pedido
                            de autorização que não se enquadre em nenhum outro"*.

                            Ela não é LINHA da tabela (a 027 a apagou e passou as
                            autorizações dela para `fkey_tipo = NULL`), e está
                            certo assim: "não se encaixa em nenhum" é a AUSÊNCIA
                            de tipo, não um tipo a mais. Por isso o texto dela
                            mora aqui, ao lado do `value=""`, e não no banco. */}
                        <option value="">Genérica</option>
                        {tipos.map((t) => (
                          <option key={t.id} value={String(t.id)}>
                            {t.nome}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="aut-tipo">Tipo</label>
                    </div>
                    <div className="pt-3">
                      <CustomDica
                        lado="left"
                        texto={
                          <div className="text-start">
                            <p className="mb-1"><strong>Desconto</strong> — abater do preço de uma venda.</p>
                            <p className="mb-1"><strong>Material extra</strong> — o instalador precisou de material além do previsto.</p>
                            <p className="mb-1"><strong>Compra</strong> — comprar algo que não está numa venda.</p>
                            <p className="mb-1"><strong>Cortesia</strong> — dar algo sem cobrar.</p>
                            <p className="mb-0"><strong>Genérica</strong> — não se encaixa em nenhum dos outros.</p>
                          </div>
                        }
                      />
                    </div>
                    </div>
                    {/* E O ESCOLHIDO SE EXPLICA SOB O CAMPO, sem passar o mouse.
                        Os dois convivem porque respondem perguntas diferentes: o
                        `ⓘ` é para ESCOLHER (compara os cinco), esta linha é para
                        CONFERIR o que já está escolhido — e ela fica visível,
                        que é o que a lei visual manda para o que a pessoa precisa
                        para agir. */}
                    <p className="small text-secondary mt-1 mb-0">
                      {tipo?.descricao ?? TIPO_GENERICA}
                    </p>
                  </div>

                  {/* ⛔ O FORNECEDOR FICA FORA DO BLOCO DO CLIENTE, de propósito.
                      O bloco abaixo só aparece nos tipos que pedem cliente
                      (Desconto) — e o fornecedor importa justamente nos OUTROS.
                      Relendo os casos reais em 11/09, TODOS nomeiam um: a PC
                      Estofados que errou o cálculo, a Bess do tecido, a Cortitex
                      dos blackouts, a Hunter da medida errada. E a etapa da
                      Patrícia é dar o destino — *"vai cobrar para a PC, vai
                      cobrar para a Cortitex"*. Sem o campo, esse destino só
                      existia escrito no meio de uma frase do chat.

                      Texto livre por ora, decisão dele: *"ainda não vai ser
                      tabela, vamos deixar campo de texto, depois a gente
                      ajusta"*. Por isso a coluna é `cc_fornecedor_nome` — o
                      `_nome` deixa `fkey_fornecedor` livre para o dia da tabela. */}
                  <div className="col-sm-6">
                    <div className="form-floating">
                      <input
                        id="aut-fornecedor"
                        className="form-control"
                        placeholder=" "
                        value={fornecedorNome}
                        onChange={(e) => setFornecedorNome(e.target.value)}
                      />
                      <label htmlFor="aut-fornecedor">Fornecedor (opcional)</label>
                    </div>
                    <p className="small text-secondary mt-1 mb-0">
                      Quem forneceu, quando o caso envolve um — PC Estofados, Cortitex,
                      Hunter. É de quem a Patrícia vai cobrar, se houver o que cobrar.
                    </p>
                  </div>

                  {/* ── Sobre o quê: as referências comerciais ───────────── */}
                  {pedeCliente && (
                    <>
                      <div className="col-12">
                        <div className="small text-secondary">
                          <i className="bi bi-info-circle me-1"></i>
                          {tipo?.pede_pedido_venda
                            ? 'Este pedido nasce no orçamento: diga o cliente. O número do pedido de venda pode ser informado depois — a autorização fica em aberto até ele chegar.'
                            : 'Este tipo de pedido é reconhecido pelo cliente.'}
                        </div>
                      </div>
                      <div className="col-sm-4">
                        <div className="form-floating">
                          <input
                            id="aut-cliente"
                            className="form-control"
                            placeholder=" "
                            value={clienteNome}
                            onChange={(e) => setClienteNome(e.target.value)}
                            required={pedeCliente}
                          />
                          <label htmlFor="aut-cliente">Cliente *</label>
                        </div>
                      </div>
                      <div className="col-sm-4">
                        {/* inputMode="numeric": teclado de números no celular.
                            A coluna é BIGINT (078) — só dígitos entram. */}
                        <div className="form-floating">
                          <input
                            id="aut-orcamento"
                            className="form-control"
                            inputMode="numeric"
                            placeholder=" "
                            value={numeroOrcamento}
                            onChange={(e) => setNumeroOrcamento(soDigitos(e.target.value))}
                          />
                          <label htmlFor="aut-orcamento">Nº do orçamento</label>
                        </div>
                      </div>
                      <div className="col-sm-4">
                        <div className="form-floating">
                          <input
                            id="aut-pedido-venda"
                            className="form-control"
                            inputMode="numeric"
                            placeholder=" "
                            value={numeroPedidoVenda}
                            onChange={(e) => setNumeroPedidoVenda(soDigitos(e.target.value))}
                          />
                          <label htmlFor="aut-pedido-venda">Nº do pedido de venda</label>
                        </div>
                      </div>
                    </>
                  )}

                  {/* ── O quê, quanto e por quê ──────────────────────────── */}
                  <div className="col-12">
                    <div className="form-floating">
                      <input
                        id="aut-titulo"
                        className="form-control"
                        placeholder=" "
                        value={titulo}
                        onChange={(e) => setTitulo(e.target.value)}
                        required
                      />
                      <label htmlFor="aut-titulo">
                        {pedeCliente ? 'Título * — ex.: Desconto de 20% para a Maria' : 'Título * — ex.: Motor extra para a OS 123'}
                      </label>
                    </div>
                  </div>

                  <div className="col-sm-4">
                    <CustomCampoNumeroMoeda
                      id="aut-valor"
                      label="Valor (opcional)"
                      valor={valor}
                      onChange={setValor}
                    />
                  </div>
                  <div className="col-sm-4">
                    {/* maximo={100} preserva o limite que o campo antigo tinha:
                        autorização é desconto/comissão, onde passar de 100 % seria
                        erro de digitação. casas={0} porque o percentual aqui é
                        inteiro (10 %, 15 %, 40 %). */}
                    <CustomCampoNumeroPercentual
                      id="aut-percentual"
                      label="Percentual (opcional)"
                      casas={2}
                      maximo={100}
                      valor={percentual}
                      onChange={setPercentual}
                    />
                  </div>

                  <div className="col-12">
                    <div className="form-floating">
                      <textarea
                        id="aut-descricao"
                        className="form-control"
                        style={{ height: 110 }}
                        placeholder=" "
                        value={descricao}
                        onChange={(e) => setDescricao(e.target.value)}
                      />
                      <label htmlFor="aut-descricao">Descrição / justificativa</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card adminuiux-card">
              <div className="card-header bg-transparent">
                <h6 className="mb-0">Quem e onde</h6>
              </div>
              <div className="card-body">
                <div className="form-floating mb-2">
                  <select
                    id="aut-solicitante"
                    className="form-select"
                    value={solicitanteId}
                    onChange={(e) => setSolicitanteId(e.target.value)}
                  >
                    <option value="">— eu mesmo / não se aplica —</option>
                    {usuarios.map((u) => (
                      <option key={u.id} value={String(u.id)}>
                        {u.nome}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="aut-solicitante">Solicitante (em nome de quem)</label>
                </div>
                {!solicitanteId && (
                  <div className="form-floating mb-3">
                    <input
                      id="aut-solicitante-nome"
                      className="form-control"
                      placeholder=" "
                      value={solicitanteNome}
                      onChange={(e) => setSolicitanteNome(e.target.value)}
                    />
                    <label htmlFor="aut-solicitante-nome">ou nome livre (externo)</label>
                  </div>
                )}

                {/* Campo de BUSCA, não select: a lista de OS não cabe num
                    menu (dele, 05/09). Quem chega pela própria OS já vem com
                    ela preenchida pelo parâmetro da URL. */}
                <CustomCampoBusca
                  id="aut-os"
                  label="Vincular a uma OS (opcional)"
                  valor={osEscolhida}
                  onChange={(item) => { setOsEscolhida(item); setOsId(item ? String(item.id) : ''); }}
                  buscar={buscarOS}
                />
                <div className="form-text">
                  O instalador pede direto na OS, que já existe. O desconto
                  nasce antes dela — aí a referência é o cliente.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button
            type="button"
            className="btn btn-link"
            onClick={() => navigate('/autorizacoes')}
          >
            Cancelar
          </button>
          <button type="submit" className="btn btn-theme" disabled={salvando}>
            {salvando && <span className="spinner-border spinner-border-sm me-1" />}
            Registrar pedido
          </button>
        </div>
      </form>
    </CustomMolduraPagina>
  );
}
